import { createContext } from 'react';
import { decorate, observable, action, toJS } from 'mobx';

import api from '../api';

export class FormInputMultipleUpdate {
    type = null;
    existingInputs = null;
    isLoading = false;
    isSaving = false;
    saveToServer = true;
    hasUnsavedChanges = false;

    load = (existingInputs, saveToServer) => {
        this.clear();

        const distinctInputTypes = existingInputs.reduce((array, input) => {
            if (!array.some(a => a === input.type)) {
                array.push(input.type);
            }
            return array;
        }, []);

        if (!distinctInputTypes || distinctInputTypes.length !== 1) {
            throw new Error('Cannot update multiple input types at the same time.');
        }
        else {
            this.type = distinctInputTypes[0];
            this.saveToServer = saveToServer;
            this.existingInputs = existingInputs && existingInputs.length > 0 ? existingInputs : [];
        }
        return Promise.resolve();
    }

    setMetadata = (key, value) => {
        if (this.existingInputs && this.existingInputs.length > 0) {
            for (let ei = 0; ei < this.existingInputs.length; ei++) {
                let metadata = this.existingInputs[ei].metadata;

                if (!metadata) {
                    metadata = {};
                }

                if (!metadata.hasOwnProperty(key)) {
                    metadata = { ...metadata, ...{ [key]: value } };
                } else {
                    metadata[key] = value;
                }

                this.existingInputs[ei].metadata = metadata;
            }
        }
    }

    removeMetadata = (key) => {
        if (this.existingInputs && this.existingInputs.length > 0) {
            for (let ei = 0; ei < this.existingInputs.length; ei++) {
                let metadata = this.existingInputs[ei].metadata;

                if (metadata && metadata.hasOwnProperty(key)) {
                    delete metadata[key];
                }
            }
        }
    }

    getMetadata = (key) => {
        let values;

        if (this.existingInputs && this.existingInputs.length > 0) {
            values = this.existingInputs.reduce((array, input) => {
                const value = !input.metadata ? undefined : input.metadata[key];
                if (!array.some(a => a === value)) {
                    array.push(value);
                }
                return array;
            }, []);
        }

        return values && values.length === 1 ? values[0] : null;
    }

    save = (notify) => {
        const that = this;

        if (!!notify) {
            this.isSaving = true;
        }

        return new Promise((resolve, reject) => {
            if (that.hasUnsavedChanges && that.existingInputs && that.existingInputs.length > 0) {
                const options = that.existingInputs.map(input => { return toJS(input) });

                if (that.saveToServer) {
                    Promise.all(options.map(o => {
                        return api.FormInputs.update(o.id, o)
                    }))
                        .then(() => {
                            that.hasUnsavedChanges = false;
                            resolve(options);
                        })
                        .catch(() => {
                            reject();
                        })
                        .finally(() => {
                            that.isSaving = false;
                        })
                } else {
                    that.isSaving = false;
                    resolve(options);
                }
            } else {
                that.hasUnsavedChanges = false;
                that.isSaving = false;
                resolve();
            }
        })
    }

    clear = () => {
        this.type = null;
        this.existingInputs = null;
        this.hasUnsavedChanges = false;
        this.isLoading = false;
        this.isSaving = false;
        this.saveToServer = true;
    }
}

decorate(FormInputMultipleUpdate, {
    type: observable,
    existingInputs: observable,
    saveToServer: observable,
    hasUnsavedChanges: observable,
    isLoading: observable,
    isSaving: observable,
    load: action,
    save: action,
    setMetadata: action,
    removeMetadata: action,
    getMetadata: action,
    clear: action,
})

export default createContext(new FormInputMultipleUpdate());